import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@lmc-env/environment';
import { InveriteRequestRequest } from '@lmc-app/shared/interface/request/inverite-request.request';
import { CreateFlinksRequestRequest } from '@lmc-app/shared/interface/request/create-flinks-request.request';
import { ApplicationCall } from '@lmc-app/shared/interface/application-call.interface';
import { User } from '@lmc-app/shared/interface/user.interface';
import { ApplicationCallRequest } from '@lmc-app/shared/interface/request/application-call.request';
import { firstValueFrom } from 'rxjs';
import { RemovedCoApplicant } from '@lmc-app/shared/interface/removed-co-applicant.interface';
import { ApplicationNoteRequest } from '@lmc-app/core/constants/application-constants';

@Injectable({
	providedIn: 'root',
})
export class ApplicationService {
	private readonly uri: string = environment.DO_URL;

	constructor(private readonly http: HttpClient) {}

	// Application CRUD operations
	createApplication(data: any): Observable<any> {
		return this.http.post(this.uri + '/v2/applications', data);
	}

	getApplications(zohoOwnerId: string = "", keyword: string = "", stages: string[] | null = null, page: number = 1, perPage: number = 200): Observable<any> {
		const stageQuery = stages ? stages.map(stage => `&stages=${stage}`).join("") : "";
		return this.http.get(`${this.uri}/v2/applications?zoho_owner_id=${zohoOwnerId}&keyword=${keyword}${stageQuery}&page=${page}&per_page=${perPage}`);
	}

	getApplication(applicationId: string): Observable<any> {
		return this.http.get(this.uri + `/v2/applications/${applicationId}`);
	}

	getVoeStatus(applicationId: string): Observable<{ status: string }> {
		return this.http.get<{ status: string }>(this.uri + `/v2/applications/${applicationId}/voe-status`);
	}

	updateApplication(applicationId: string, request: {}): Observable<any> {
		return this.http.patch(
			this.uri + `/v2/applications/${applicationId}`,
			request
		);
	}

	getApplicationConfig(): Observable<any> {
		return this.http.get(this.uri + `/v2/application_config`);
	}

	// Rapid
	emailRapid(data: any): Observable<any> {
		return this.http.post(`${this.uri}/v2/applications/email_rapid/${data.applicationId}`, data);
	}

	// Session management
	getSessionsByEmployeeId(employeeId: string): Observable<any> {
		return this.http.get(this.uri + `/v1/application/sessions/${employeeId}`);
	}

	getSessionBySessionId(sessionId: string): Observable<any> {
		return this.http.get(this.uri + `/v1/application/session/${sessionId}`);
	}

	deleteSession(sessionId: string): Observable<any> {
		return this.http.delete(this.uri + `/v1/application/session/${sessionId}`);
	}

	saveSession(
		sessionId: string,
		data: string,
		createdBy: number
	): Observable<any> {
		if (sessionId) {
			return this.http.patch(
				this.uri + `/v1/application/session/${sessionId}`,
				{
					data: data,
				}
			);
		} else {
			return this.http.post(this.uri + '/v1/application/session', {
				data: data,
				createdBy: createdBy,
			});
		}
	}

	// Document management
	uploadDocumentToApplication(formData: FormData): Observable<any> {
		return this.http.post(this.uri + '/v1/application/document', formData);
	}

	getDocumentsByApplicationId(applicationId: string): Observable<any> {
		return this.http.get(
			this.uri + `/v1/application/document/${applicationId}`
		);
	}

	deleteDocumentByResourceId(resourceId: string): Observable<any> {
		return this.http.patch(
			this.uri + `/v1/application/document/${resourceId}`,
			{}
		);
	}

	setupApplicationFolder(data: any): Observable<any> {
		return this.http.post(this.uri + '/v1/application/folder', data);
	}

	// Inverite requests
	getInveriteRequests(applicationId: string): Observable<any> {
		return this.http.get(
			`${this.uri}/v1/applications/${applicationId}/inverite`
		);
	}

	createInveriteRequest(
		applicationId: string,
		request: InveriteRequestRequest
	): Observable<any> {
		return this.http.post(
			`${this.uri}/v1/applications/${applicationId}/inverite`,
			request
		);
	}

	deleteInveriteRequest(
		applicationId: string,
		inveriteId: string
	): Observable<any> {
		return this.http.delete(
			`${this.uri}/v1/applications/${applicationId}/inverite/${inveriteId}`
		);
	}

	// Email management
	sendEmailToApplicantByApplicationId(
		applicationId: string,
		request: any
	): Observable<any> {
		return this.http.post(
			`${this.uri}/v1/applications/${applicationId}/emails`,
			request
		);
	}

	getEmailsByApplicationId(applicationId: string): Observable<any> {
		return this.http.get(`${this.uri}/v1/applications/${applicationId}/emails`);
	}

	// Application calls management
	queryApplicationCalls(
		applicationId?: string,
		startTime?: string,
		endTime?: string,
		status?: string,
		purpose?: string
	): Observable<ApplicationCall[]> {
		let params = new URLSearchParams();

		if (applicationId) params.append('applicationId', applicationId);
		if (startTime) params.append('startTime', startTime);
		if (endTime) params.append('endTime', endTime);
		if (status) params.append('status', status);
		if (purpose) params.append('purpose', purpose);

		const queryString = params.toString();
		const url = queryString
			? `${this.uri}/v1/applications/calls?${queryString}`
			: `${this.uri}/v1/applications/calls`;

		return this.http.get<ApplicationCall[]>(url);
	}

	getApplicationCall(
		applicationId: string,
		applicationCallId: string
	): Observable<ApplicationCall> {
		return this.http.get<ApplicationCall>(
			`${this.uri}/v1/applications/${applicationId}/calls/${applicationCallId}`
		);
	}

	getApplicationCalls(
		applicationId: string,
		startTime?: string
	): Observable<ApplicationCall[]> {
		let params = new HttpParams();
		if (startTime) {
			params = params.set('startTime', startTime);
		}

		return this.http.get<ApplicationCall[]>(
			`${this.uri}/v1/applications/${applicationId}/calls`,
			{ params }
		);
	}

	createApplicationCall(
		applicationId: string,
		request: ApplicationCallRequest
	): Observable<any> {
		return this.http.post<any>(
			`${this.uri}/v1/applications/${applicationId}/calls`,
			request
		);
	}

	updateApplicationCall(
		applicationId: string,
		callId: string,
		request: ApplicationCallRequest
	): Observable<any> {
		return this.http.patch<any>(
			`${this.uri}/v1/applications/${applicationId}/calls/${callId}`,
			request
		);
	}

	deleteApplicationCall(
		applicationId: string,
		callId: string,
		userId: number
	): Observable<any> {
		return this.http.delete<any>(
			`${this.uri}/v1/applications/${applicationId}/calls/${callId}?userId=${userId}`
		);
	}

	// Other v1 endpoints (not application-specific but used together)
	createFlinksRequest(request: CreateFlinksRequestRequest): Observable<any> {
		return this.http.post(`${this.uri}/v1/flinks`, request);
	}

	refreshFlinksRequestStatus(applicationId: string): Observable<any> {
		return this.http.get(`${this.uri}/v1/flinks/${applicationId}`);
	}

	getModuleField(module: string, field: string): Observable<any> {
		return this.http.get(`${this.uri}/v1/zoho/${module}/fields/${field}`);
	}

	getModuleFields(module: string): Observable<any> {
		return this.http.get(`${this.uri}/v1/zoho/${module}/fields`);
	}

	getUsers(): Observable<User[]> {
		return this.http.get<User[]>(`${this.uri}/v1/users`);
	}

	// Stage management with note
	updateApplicationStageWithNote(
		applicationId: string,
		stage: string,
		note: any,
		stageHistoryId: string,
		zohoUserId: string,
		dealName: string,
		createdBy: string
	): Observable<any> {
		const data = {
			stage,
			stageHistoryId,
			zohoUserId,
			dealName,
			createdBy,
			note,
		};
		return this.http.patch(
			`${this.uri}/v1/applications/${applicationId}/stage`,
			data
		);
	}

	getWorkdriveExternalLink(resourceId: string): Observable<any> {
		return this.http.get(`${this.uri}/v1/applications/workdrive/file/externalLink/${resourceId}`);
	}

	// Timeline (Zoho __timeline passthrough)
	getApplicationTimeline(applicationId: string, page: number = 1, perPage: number = 200, pageToken?: string): Observable<any> {
		// Zoho rejects per_page together with page_token (AMBIGUITY_DURING_PROCESSING)
		const params = pageToken
			? new HttpParams().set('page_token', pageToken)
			: new HttpParams().set('page', page).set('per_page', perPage);
		return this.http.get(`${this.uri}/v2/applications/${applicationId}/timeline`, { params });
	}

	// Co-applicant removal
	removeCoApplicant(applicationId: string, reason: string, overrideType: string): Observable<{ id: number }> {
		return this.http.post<{ id: number }>(`${this.uri}/v2/applications/${applicationId}/co_applicant/remove`, { reason, overrideType });
	}

	// Restore existing co-applicant
	restoreCoApplicant(applicationId: string, historyId: number, reason: string, overrideType: string): Observable<{ id: number }> {
		return this.http.post<{ id: number }>(`${this.uri}/v2/applications/${applicationId}/co_applicant/restore`, { historyId, reason, overrideType });
	}

	// The application's removals not yet restored
	getRestorableCoApplicants(applicationId: string): Observable<RemovedCoApplicant[]> {
		return this.http.get<RemovedCoApplicant[]>(`${this.uri}/v2/applications/${applicationId}/co_applicant/removals`);
	}

	// All of co-applicants removed or restored
	getCoApplicantHistory(applicationId: string): Observable<RemovedCoApplicant[]> {
		return this.http.get<RemovedCoApplicant[]>(`${this.uri}/v2/applications/${applicationId}/co_applicant/history`);
	}

	// Creates a note for any removal/restore
	createApplicationNote(applicationId: string, note: ApplicationNoteRequest): Observable<{ app_note_id: number }> {
		return this.http.post<{ app_note_id: number }>(`${this.uri}/v2/applications/${applicationId}/notes`, note);
	}

	// Pins or unpins a note to the top of the note list
	setApplicationNotePinned(applicationId: string, noteId: number, pinned: boolean): Observable<{ app_note_id: number, pinned: boolean }> {
		return this.http.patch<{ app_note_id: number, pinned: boolean }>(`${this.uri}/v2/applications/${applicationId}/notes/${noteId}/pin`, { pinned });
	}
}
